/* eslint-disable import/no-unresolved */
/* eslint-disable security/detect-object-injection */
import { isEqual } from "lodash";
import {
  RiderBenefitType,
  RiderPlanOption,
  RiderSubPlanOptionType,
  validSubPlans,
  partners
} from "src/config/constant";
import { IAddRider, PRODUCT_ID, Products } from "./ProposalDetails/interfaces";

/* eslint-disable import/prefer-default-export */
const extractLoanKeys = (loanDetails: any) => {
  const filteredLoanDetails = {
    ...loanDetails.loan,
    ...loanDetails.plan,
    ...loanDetails.premiumFunding,
    ...loanDetails.premiumDetails
  } as any;
  delete filteredLoanDetails.totalPayment;
  return filteredLoanDetails;
};
export const checkLoanKeysDirty = (prev: any, curr: any) => {
  console.log("diff keys", extractLoanKeys(prev), extractLoanKeys(curr));
  return isEqual(extractLoanKeys(prev), extractLoanKeys(curr));
};

export const GENERAL_DETAILS_MAPPING = {
  companyName: (partnerId: string) => ({
    ageas: "Ageas Federal Bank",
    ...(partnerId === "federal" ? { federal: "Federal Bank" } : { idbi: "IDBI Bank" })
  })
};

export const loanDetailsProductWise: Record<string, any> = {
  [PRODUCT_ID.GHS]: {
    term: "Months"
  },
  [PRODUCT_ID.GLS]: {
    term: "Years"
  },
  [PRODUCT_ID.GHS_FOR_JIO]: {
    term: "Months"
  },
  [PRODUCT_ID.GHS_IDBI]: {
    term: "Months"
  },
  [PRODUCT_ID.GCS]: {
    term: "Months"
  }
};

export const products: Products = {
  GLS: 9069,
  GHS: 9078,
  GHS_FOR_JIO: 9099,
  GHS_IDBI: 9101,
  GCS: 9086,
  GCS_SREI: 9109,
  GCS_CAPRI: 9112,
  GCS_SOLFIN: 9123
};
export const ProductNameMapper: Record<number, string> = {
  9069: "GLS",
  9078: "GHS",
  9099: "GHS_FOR_JIO",
  9101: "GHS_IDBI",
  9086: "GCS"
};

export const getAssignmentFlag = (toShow: boolean) => {
  if (!toShow) return false;
  return true;
};

export const prepareRiderPayload = (leadId: string, data: Record<string, any>) => {
  const obj = {
    leadId,
    riderDetails: data?.map((rider: Record<string, any>) => ({
      riderBenefitType: rider?.benefitTypeCode,
      riderPlanOption: rider?.planOptionCode,
      riderSumAssured: rider?.sumAssured,
      riderPT: rider?.policyTerm,
      riderPPT: rider?.premiumPolicyTerm,
      riderProductSelection: rider?.productSelectionCode,
      ...(rider?.riderId && { riderId: rider?.riderId }),
      ...(rider?.subPlanOptionCode && { riderSubPlanOption: rider?.subPlanOptionCode }),
      ...(rider?.emi && { emiAmount: rider?.emi }),
      ...(rider?.id && { id: rider?.id })
    }))
  };
  return obj;
};

export const showRiderConditionalField = (
  currentRider: IAddRider,
  masterMapping: Record<string, any>
) => {
  const isGCIR = currentRider?.productSelection === "GCIR";

  return {
    productSelection: Object.keys(masterMapping || {})?.length,

    benefitType: Object.keys(masterMapping?.[currentRider?.productSelectionCode] || {})?.length,

    planOptions:
      !isGCIR &&
      Object.keys(
        masterMapping?.[currentRider?.productSelectionCode]?.[currentRider?.benefitTypeCode] || {}
      )?.length,

    subPlanOptions:
      !isGCIR &&
      Object.keys(
        masterMapping?.[currentRider?.productSelectionCode]?.[currentRider?.benefitTypeCode]?.[
          currentRider?.planOptionCode!
        ] || {}
      )?.length
  };
};

export const riderValidator = (
  ridersDetails: IAddRider[]
): { combined: string; riders: string[] } => {
  const validationObj = { combined: "", riders: [""] };
  if (!ridersDetails || ridersDetails.length === 0) {
    return Object.assign(validationObj, { combined: "Riders details are empty" });
  }

  const gacrRiders = ridersDetails.filter((r) => r.productSelection === "GACR");

  const gcirRiders = ridersDetails.filter((r) => r.productSelection === "GCIR");

  if (gcirRiders.length > 1) {
    return Object.assign(validationObj, {
      combined: "Only one GCIR rider can be selected"
    });
  }

  if (gcirRiders.length === 1) {
    const plan = gcirRiders[0].benefitTypeCode;

    if (plan !== "CP" && plan !== "CPP") {
      return Object.assign(validationObj, {
        combined: "GCIR rider must be either CP or CPP"
      });
    }
  }

  // 1. Check if "comprehensive" or "emiSupport" benefits are selected in combination with any other plan option
  const hasComprehensive = ridersDetails.some(
    (rider) => rider.planOptionCode === RiderPlanOption.COMPREHENSIVE
  );
  const hasEMISupport = ridersDetails.some(
    (rider) => rider.planOptionCode === RiderPlanOption.EMISUPPORT
  );
  const hasOtherPlans = ridersDetails.some(
    (rider) =>
      rider.planOptionCode !== RiderPlanOption.COMPREHENSIVE &&
      rider.planOptionCode !== RiderPlanOption.EMISUPPORT &&
      rider.productSelection !== "GCIR"
  );
  if ((hasComprehensive || hasEMISupport) && ridersDetails.length > 1) {
    return Object.assign(validationObj, {
      combined:
        "Comprehensive or EMI support benefits cannot be selected in combination with any other plan option"
    });
  }
  if (hasOtherPlans && gacrRiders.length > 2) {
    return Object.assign(validationObj, {
      combined: "More than 2 Riders cannot be opted for current product"
    });
  }

  const subPlansSelected = ridersDetails
    .filter((r) => r.productSelection === "GACR")
    .map((rider) => rider.subPlanOptionCode);
  const deathSuperSet = new Set(validSubPlans[RiderPlanOption.DEATH]);
  const disabilitySuperSet = new Set(validSubPlans[RiderPlanOption.DISABILITY]);

  const validRiders =
    ridersDetails?.filter((r) => r.productSelection === "GACR" && r.benefitTypeCode) || [];

  const isSingleValidAdditional =
    validRiders.length === 1 && validRiders[0].benefitTypeCode === RiderBenefitType.ADDITIONAL;

  const isSingleValidOther =
    validRiders.length === 1 &&
    validRiders[0].benefitTypeCode === RiderBenefitType.ACCELERATED &&
    !validSubPlans[RiderPlanOption.DEATH]?.includes(
      validRiders[0].subPlanOptionCode as RiderSubPlanOptionType
    );

  const areTwoAllAdditional =
    validRiders.length === 2 &&
    validRiders.every((r) => r.benefitTypeCode === RiderBenefitType.ADDITIONAL);

  const hasValidBenefitType = isSingleValidAdditional || areTwoAllAdditional || isSingleValidOther;

  if (validRiders.length && !hasValidBenefitType) {
    return {
      ...validationObj,
      combined: "Riders has invalid Benefit Type"
    };
  }

  if (gacrRiders.length > 1) {
    if (
      subPlansSelected.every((value) => deathSuperSet.has(value as RiderSubPlanOptionType)) ||
      subPlansSelected.every((value) => disabilitySuperSet.has(value as RiderSubPlanOptionType))
    ) {
      return Object.assign(validationObj, {
        combined: "Rider has invalid Sub Plan Option"
      });
    }
  }

  for (let i = 0; i < ridersDetails.length; i += 1) {
    const rider = ridersDetails?.[i];
    if (!rider) {
      validationObj.riders[i] = "Rider is missing";
      return validationObj;
    }
    const { planOptionCode, subPlanOptionCode } = rider;

    // 5. Check if rider sub plan option is in line with its plan option
    const subPlan = subPlanOptionCode ?? null;
    if (subPlan && validSubPlans[planOptionCode!]) {
      if (!validSubPlans[planOptionCode!]?.includes(subPlan as RiderSubPlanOptionType)) {
        validationObj.riders[i] = `For ${planOptionCode} plan option, ${validSubPlans[
          planOptionCode!
        ]
          ?.map((v) => (v === null ? "no sub plan" : v))
          .join(" or ")} sub plan options are allowed`;

        return validationObj;
      }
    }
  }
  return Object.assign(validationObj, {
    combined: ""
  });
};

export const prePopulateST1Code = [partners.SREI];
